import {
	FieldDefUuid,
	getLogicOnFieldsFromField,
	RunnitNode,
	RunnitNodeField,
	RunnitNodeFieldGroup,
	RunnitNodeRunInputValue,
	RunnitNodeFieldType,
	RUNNIT_NODE_DEF_TOOL_APP_TYPE,
	RUNNIT_NODE_FIELD_TYPE,
} from '@run-diffusion/shared';

export type BatchMediaVariableFieldMode = 'image' | 'video' | 'asset3d';

export const BATCH_MEDIA_VARIABLE_FIELD_MODE: Record<BatchMediaVariableFieldMode, BatchMediaVariableFieldMode> = {
	image: 'image',
	video: 'video',
	asset3d: 'asset3d',
};

export function getBatchMediaVariableFieldMode (fieldType: RunnitNodeFieldType | null | undefined): BatchMediaVariableFieldMode | null {
	if (fieldType === RUNNIT_NODE_FIELD_TYPE.IMG || fieldType === RUNNIT_NODE_FIELD_TYPE.IMGS) return BATCH_MEDIA_VARIABLE_FIELD_MODE.image;
	if (fieldType === RUNNIT_NODE_FIELD_TYPE.VIDEO) return BATCH_MEDIA_VARIABLE_FIELD_MODE.video;
	if (fieldType === RUNNIT_NODE_FIELD_TYPE.ASSET_3D) return BATCH_MEDIA_VARIABLE_FIELD_MODE.asset3d;
	return null;
}

/**
 * Returns true if the node's nodeDef is shaped to support the batch wizard
 * (resolved RUNNIT app type and batchable). Permission is checked separately.
 */
export function isNodeBatchEligible (node: RunnitNode | null | undefined): boolean {
	if (!node || typeof node.nodeDef === 'string') return false;
	return !!(
		node.nodeDef.appType === RUNNIT_NODE_DEF_TOOL_APP_TYPE.RUNNIT &&
		node.nodeDef.batchable
	);
}

function walkFields (
	fields: (RunnitNodeField | RunnitNodeFieldGroup)[] | undefined,
	out: RunnitNodeField[],
): void {
	if (!fields || !fields.length) return;
	for (const f of fields) {
		if ((f as RunnitNodeFieldGroup).__rgroup) {
			walkFields((f as RunnitNodeFieldGroup).fields, out);
		} else if ((f as RunnitNodeField).__rfield) {
			const field = f as RunnitNodeField;
			if (field.type !== RUNNIT_NODE_FIELD_TYPE.HEADER) {
				out.push(field);
			}
		}
	}
}

/** Flat leaf fields (excludes HEADER), in definition order. */
export function flattenRunnitNodeFields (node: RunnitNode): RunnitNodeField[] {
	const tree =
		node.nodeDef && typeof node.nodeDef !== 'string'
			? node.nodeDef.fields
			: [];
	const out: RunnitNodeField[] = [];
	walkFields(tree || [], out);
	return out;
}

/** Mirrors RunnitDynamicField visibility for batch UI (admin + logic + autoSet hide). */
export function isRunnitFieldVisibleForBatchUi (
	field: RunnitNodeField,
	inputValues: Record<FieldDefUuid, RunnitNodeRunInputValue>,
	isAdmin: boolean,
): boolean {
	return !!(field && field.__rfield && isRunnitFieldOrGroupVisibleForUi(field, inputValues, isAdmin));
}

function isRunnitFieldOrGroupVisibleForUi (
	fieldOrGroup: RunnitNodeField | RunnitNodeFieldGroup,
	inputValues: Record<FieldDefUuid, RunnitNodeRunInputValue>,
	isAdmin: boolean,
): boolean {
	const hasField = !!(
		fieldOrGroup &&
		((fieldOrGroup as RunnitNodeField).__rfield || (fieldOrGroup as RunnitNodeFieldGroup).__rgroup)
	);
	const logicOnFields = getLogicOnFieldsFromField(fieldOrGroup as RunnitNodeField, inputValues);
	const showsCondition = !logicOnFields.allShows.length || logicOnFields.triggeredShows.length;
	const adminCondition = !(fieldOrGroup as RunnitNodeField).adminOnly || isAdmin;
	let autoSetCondition = true;
	const hasAutoSetLogic =
		(fieldOrGroup as RunnitNodeField).logic &&
		(fieldOrGroup as RunnitNodeField).logic.field &&
		Object.values((fieldOrGroup as RunnitNodeField).logic.field).some((logic: { autoSet?: unknown }) => !!logic?.autoSet);
	if (hasAutoSetLogic && logicOnFields.autoSetVisible === false) {
		autoSetCondition = false;
	}
	return !!(hasField && showsCondition && adminCondition && autoSetCondition);
}

export function filterRenderableAdvancedFields (
	fields: (RunnitNodeField | RunnitNodeFieldGroup)[] | undefined,
	inputValues: Record<FieldDefUuid, RunnitNodeRunInputValue>,
	isAdmin: boolean,
): (RunnitNodeField | RunnitNodeFieldGroup)[] {
	const result: (RunnitNodeField | RunnitNodeFieldGroup)[] = [];
	for (const item of fields || []) {
		if ((item as RunnitNodeFieldGroup).__rgroup) {
			if (!isRunnitFieldOrGroupVisibleForUi(item, inputValues, isAdmin)) continue;
			const cleaned = filterRenderableAdvancedFields((item as RunnitNodeFieldGroup).fields || [], inputValues, isAdmin);
			if (cleaned.length > 0) {
				result.push({ ...item, fields: cleaned } as RunnitNodeFieldGroup);
			}
		} else if (
			(item as RunnitNodeField).__rfield &&
			(item as RunnitNodeField).type !== RUNNIT_NODE_FIELD_TYPE.MODEL_SINGLE_SELECT &&
			// A field declaring ANY primaryForm is already rendered in the drawer's
			// main section, so it must not appear in Advanced as well. This holds
			// regardless of whether PrimaryForm can actually draw that type: a
			// `primaryForm: 'CHIP'` UPSCALE_FACTOR_SLIDER has no chip renderer and
			// still stays in advancedFields for PrimaryForm's sake, but the drawer
			// draws it up top via RunnitDynamicField, which handles every type.
			!(item as RunnitNodeField).display?.primaryForm &&
			isRunnitFieldOrGroupVisibleForUi(item, inputValues, isAdmin)
		) {
			result.push(item);
		}
	}
	return result;
}

/**
 * Advanced fields an autoSet hides. filterRenderableAdvancedFields leaves them out
 * of the drawer's Advanced panel, but they still have to be mounted: the field
 * component is what writes the autoSet value (a video's duration, say) into
 * inputValues, and the price is computed from inputValues. A field with a
 * primaryForm is left out because the drawer's main section already mounts it.
 */
export function collectAutoSetHiddenFields (
	fields: (RunnitNodeField | RunnitNodeFieldGroup)[] | undefined,
	inputValues: Record<FieldDefUuid, RunnitNodeRunInputValue>,
): RunnitNodeField[] {
	const leaves: RunnitNodeField[] = [];
	walkFields(fields, leaves);
	return leaves.filter((field) => (
		!field.display?.primaryForm &&
		getLogicOnFieldsFromField(field, inputValues).autoSetVisible === false
	));
}

export function collectBatchVariableFieldCandidates (
	node: RunnitNode,
	inputValues: Record<FieldDefUuid, RunnitNodeRunInputValue>,
	isAdmin: boolean,
): RunnitNodeField[] {
	return flattenRunnitNodeFields(node).filter((f) => isRunnitFieldVisibleForBatchUi(f, inputValues, isAdmin));
}

export function batchBaseSettingsFields (
	node: RunnitNode,
	inputValues: Record<FieldDefUuid, RunnitNodeRunInputValue>,
	isAdmin: boolean,
	excludeFieldDefUuid: FieldDefUuid | null,
): RunnitNodeField[] {
	return collectBatchVariableFieldCandidates(node, inputValues, isAdmin).filter(
		(f) => !excludeFieldDefUuid || f.fieldDefUuid !== excludeFieldDefUuid,
	);
}
